import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Bank } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { BANK_INTERFACE_SELECTION_ENABLED_TENANTS } from "@/lib/constants/feature-flags";
import { useTenant } from "@/providers/session";
import { useGetAllBankConnections } from "@/queries/bank-connections/get-all";
import { useConnectBank } from "@/queries/banks/connect";
import { BankSelectionStep } from "./connection-steps/connection-step-bank-selection";
import { ConnectionCompleteStep } from "./connection-steps/connection-step-complete";
import { ConnectionStepFinapi } from "./connection-steps/connection-step-finapi";
import { ConnectionStepFintsWarning } from "./connection-steps/connection-step-fints-warning";
import { ConnectionStepImportSettings } from "./connection-steps/connection-step-import-settings";

type Props = {
  open: boolean;
  onCancel: () => void;
  setStep: (step: number) => void;
  step: number;
  error: boolean;
  abort: boolean;
  success: boolean;
};

export const CreateBankConnectionForm = ({
  open = false,
  onCancel,
  step = 0,
  error = false,
  abort = false,
  success = false,
  setStep,
}: Props) => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  // FIXME: This should be a form state
  const [txnImportEnabled, setTxnImportEnabled] = useState(true);
  const [startImportDate, setStartImportDate] = useState<Date | undefined>(
    new Date(),
  );
  const [connectionName, setConnectionName] = useState<string>("");
  const [selectedInterface, setSelectedInterface] = useState<
    "FINTS_SERVER" | "XS2A" | null
  >(null);

  const [connectBank, { loading: connectBankLoading }] = useConnectBank();
  const [webformId, setWebformId] = useState<string | null>(null);
  const { bankConnections } = useGetAllBankConnections();

  const bankAlreadyConnected =
    !!selectedBank &&
    bankConnections.some((c) => c.finapiBankId === selectedBank.id);

  const fintsRequired = selectedBank?.shouldAddFints ?? false;

  const [fintsAcknowledged, setFintsAcknowledged] = useState(false);

  // Feature flag for interface selection
  const isInterfaceSelectionEnabled =
    BANK_INTERFACE_SELECTION_ENABLED_TENANTS.includes(tenantId);

  const handleBankSelection = (bank: Bank) => {
    setSelectedBank(bank);
    setStep(1); // Move to import settings step
    setFintsAcknowledged(false);
  };

  const handleConfirm = async () => {
    if (!selectedBank) return;

    const interfaces =
      isInterfaceSelectionEnabled && selectedInterface
        ? [selectedInterface]
        : undefined;

    const { id } = await connectBank({
      bankId: selectedBank.id,
      txnImportEnabled,
      startImportDate: startImportDate ?? new Date(),
      connectionName,
      interfaces,
    });

    if (id) {
      setWebformId(id);
      setStep(3); // Move to FinAPI step
    }
  };

  const handleProceedFromSettings = () => {
    if (fintsRequired) {
      setFintsAcknowledged(false);
      setStep(2); // Show FinTS warning before connecting
      return;
    }
    void handleConfirm();
  };

  const handleOnCancel = () => {
    setSelectedBank(null);
    setWebformId(null);
    setSelectedInterface(null);
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleOnCancel}>
      <DialogContent className="sm:max-w-[600px] overflow-hidden">
        <div className="w-full max-w-2xl">
          {step === 0 && (
            <BankSelectionStep
              onSelect={handleBankSelection}
              loading={connectBankLoading}
            />
          )}

          {step === 1 && selectedBank && (
            <ConnectionStepImportSettings
              bank={selectedBank}
              txnImportEnabled={txnImportEnabled}
              startImportDate={startImportDate}
              connectionName={connectionName}
              bankAlreadyConnected={bankAlreadyConnected}
              setTxnImportEnabled={setTxnImportEnabled}
              setStartImportDate={setStartImportDate}
              setConnectionName={setConnectionName}
              isInterfaceSelectionEnabled={isInterfaceSelectionEnabled}
              selectedInterface={selectedInterface}
              setSelectedInterface={setSelectedInterface}
            />
          )}
          {step === 2 && selectedBank && (
            <ConnectionStepFintsWarning
              bank={selectedBank}
              acknowledged={fintsAcknowledged}
              onAcknowledgeChange={setFintsAcknowledged}
            />
          )}

          {step === 3 && webformId && selectedBank && (
            <ConnectionStepFinapi webformId={webformId} />
          )}

          {step === 4 && selectedBank && (
            <ConnectionCompleteStep
              bank={selectedBank}
              error={error}
              abort={abort}
              success={success}
            />
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button onClick={handleOnCancel} variant="outline">
            {t("cancel")}
          </Button>
          {step === 1 && (
            <Button
              onClick={handleProceedFromSettings}
              disabled={
                (bankAlreadyConnected && connectionName.trim().length === 0) ||
                (isInterfaceSelectionEnabled && !selectedInterface)
              }
            >
              {fintsRequired ? t("next") : t("confirm")}
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={handleConfirm}
              disabled={
                !fintsAcknowledged ||
                (bankAlreadyConnected && connectionName.trim().length === 0)
              }
            >
              {t("confirm")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
